import gql from "graphql-tag";

import { useLoggedInAuth } from "auth/AuthContext";
import { isVerifiedLoginMethod } from "auth/utils";
import { Query } from "components/Query/Query";
import {
  AccountIdentities,
  AccountIdentitiesData,
  IdentityFragment,
  NablaProduct,
  SupportedLocale,
} from "generated/account";

import {
  getGqlLoginMethod,
  getIdentityFromGql,
  getOrganizationFromGql,
  Identity,
  KnownIdentityFragment,
} from "./utils";

gql`
  # schema = ACCOUNT

  fragment AccountOrganizationSummary on Organization {
    uuid
    displayName
    stringId
    product
    customerEnvironment
  }

  fragment CopilotApiDeveloperSummary on CopilotApiDeveloper {
    uuid
    organization {
      ...AccountOrganizationSummary
    }
  }

  fragment Identity on Identity {
    uuid
    acceptableLoginMethods

    ... on CopilotApiDeveloperIdentity {
      developer {
        ...CopilotApiDeveloperSummary
      }
    }
  }

  fragment Identities on Account {
    uuid
    identities {
      ...Identity
    }
  }

  query AccountIdentities {
    me {
      ...Identities
      email
      locale
      timezone
      hasPassword
      mfaState {
        ... on SetupMfaState {
          supportedMethods {
            ... on TotpMfaMethod {
              isSetup
            }
          }
        }
        ... on NotSetupMfaState {
          supportedMethods {
            ... on TotpMfaMethod {
              isSetup
              tentativeQrCode
            }
          }
        }
      }
    }
  }
`;

export type IdentitiesQueryIdentities = {
  organization: OrganizationSummary;
  identitiesForOrganization: Identity[];
}[];

export type IdentitiesQueryData = {
  identities: IdentitiesQueryIdentities;
  accountEmail: string;
  accountHasPassword: boolean;
  accountLocale: SupportedLocale;
  accountTimezone: TimeZone;
  mfaState: AccountIdentitiesData["mfaState"];
};

type OrganizationSummary = {
  uuid: UUID;
  stringId: string;
  displayName: string;
  product: NablaProduct;
};

/**
 * Lets users connected with an account token switch between all the identities
 * associated to their account. Shouldn't be used when there is no logged-in
 * user, or if the user isn't logged in with an account token.
 */
export const IdentitiesQuery = ({
  children,
}: {
  children: (props: IdentitiesQueryData) => JSX.Element;
}) => {
  const auth = useLoggedInAuth();

  const currentLoginMethod = getGqlLoginMethod(auth.currentSessionLoginMethod);
  if (!isVerifiedLoginMethod(currentLoginMethod)) return null;

  return (
    <Query query={AccountIdentities}>
      {({ identities, email, locale, timezone, hasPassword, mfaState }) =>
        children({
          identities: identities
            .filter(isCopilotApiUserIdentity)
            .map((identity) => ({ identity }))
            .groupByToEntries(
              ({ identity }) => getOrganizationFromGql(identity).uuid,
            )
            .map(([_, identitiesForOrganization]) => ({
              organization: getOrganizationFromGql(
                identitiesForOrganization[0].identity,
              ),
              identitiesForOrganization: identitiesForOrganization
                .mapNotNull((it) => getIdentityFromGql(it, currentLoginMethod))
                .sortAsc((it) => it.uuid),
            }))
            .sortAsc(({ organization }) => organization.displayName),
          accountEmail: email,
          accountLocale: locale,
          accountTimezone: timezone,
          accountHasPassword: hasPassword,
          mfaState,
        })
      }
    </Query>
  );
};

const isCopilotApiUserIdentity = (
  identity: IdentityFragment,
): identity is KnownIdentityFragment =>
  identity.__typename === "CopilotApiDeveloperIdentity";
